import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errMsg, inr } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { PageShell, Loader } from "../components/SiteChrome";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const { items, clear, count } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: "", pincode: "" });
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => setProducts([]));
  }, []);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" }));
  }, [user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const meta = (id) => products?.find((p) => p.product_id === id);
  const lines = Object.entries(items).filter(([id]) => meta(id));
  const total = lines.reduce((s, [id, q]) => s + q * meta(id).price, 0);

  const pay = async (e) => {
    e.preventDefault();
    setMsg("");
    if (count === 0) { setMsg("Your cart is empty."); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        items: Object.entries(items).map(([product_id, qty]) => ({ product_id, qty })),
      };
      const { data } = await api.post("/payments/create-order", payload);
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load the payment window. Check your connection.");
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "Kaffgum",
        description: "Caffeinated chewing gum",
        order_id: data.razorpay_order_id,
        prefill: { name: data.name, email: data.email, contact: data.phone },
        theme: { color: "#D9A441" },
        handler: async (res) => {
          try {
            await api.post("/payments/verify", {
              order_id: data.order_id,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            clear();
            navigate(`/order/${data.order_id}`);
          } catch (err) {
            setMsg(errMsg(err));
            setBusy(false);
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.on("payment.failed", () => { setMsg("Payment failed. Nothing was charged — try again."); setBusy(false); });
      rzp.open();
    } catch (err) {
      setMsg(errMsg(err));
      setBusy(false);
    }
  };

  return (
    <PageShell kicker="Checkout" title="Nearly there." sub="Where should the tin go?">
      {!products ? <Loader /> : count === 0 ? (
        <div className="wrap" style={{ textAlign: "center", padding: "40px 0 80px" }}>
          <p style={{ color: "var(--ink-dim)" }} data-testid="checkout-empty-msg">Your cart is empty.</p>
          <Link className="kbtn" to="/shop" style={{ maxWidth: 220, margin: "18px auto 0" }} data-testid="checkout-goto-shop">Go to shop</Link>
        </div>
      ) : (
        <form className="co-wrap" onSubmit={pay} data-testid="checkout-form">
          <div className="co-form">
            <div className="co-2col">
              <label className="field"><span>Full name</span>
                <input required value={form.name} onChange={set("name")} placeholder="Your name" data-testid="checkout-name-input" /></label>
              <label className="field"><span>Email</span>
                <input required type="email" value={form.email} onChange={set("email")} placeholder="name@email.com" data-testid="checkout-email-input" /></label>
            </div>
            <label className="field"><span>Phone</span>
              <input required type="tel" minLength={8} maxLength={15} value={form.phone} onChange={set("phone")} placeholder="9000000000" data-testid="checkout-phone-input" /></label>
            <label className="field"><span>Address</span>
              <input required value={form.address} onChange={set("address")} placeholder="Street, building, flat" data-testid="checkout-address-input" /></label>
            <div className="co-2col">
              <label className="field"><span>City</span>
                <input required value={form.city} onChange={set("city")} placeholder="City" data-testid="checkout-city-input" /></label>
              <label className="field"><span>Pincode</span>
                <input required minLength={4} maxLength={10} value={form.pincode} onChange={set("pincode")} placeholder="560001" data-testid="checkout-pincode-input" /></label>
            </div>
            <p className={"form-msg" + (msg ? " err" : "")} role="status" data-testid="checkout-msg">{msg}</p>
          </div>
          <aside className="co-sum">
            {lines.map(([id, q]) => (
              <div className="co-line" key={id} data-testid={`checkout-line-${id}`}>
                <span>{meta(id).name} × {q}</span>
                <span>{inr(q * meta(id).price)}</span>
              </div>
            ))}
            <div className="co-line"><span>Delivery</span><span>{total >= 499 ? "Free" : "Calculated by courier"}</span></div>
            <div className="co-tot"><span>Total</span><b data-testid="checkout-total">{inr(total)}</b></div>
            <button className="kbtn wide" type="submit" disabled={busy} data-testid="checkout-pay-btn">
              {busy ? "Opening payment\u2026" : `Pay ${inr(total)}`}
            </button>
            <p className="dnote" style={{ marginTop: 12 }}>Secure payment via Razorpay — UPI, cards, netbanking.</p>
          </aside>
        </form>
      )}
    </PageShell>
  );
}
